<template>
  <!-- Default avatar for MANUAL (off-catalog) order-box lines. These items
       never carry a product photo (avatarUrl/productAvatarUrl are always null
       from the API), so instead of a broken <img> we render a branded monogram
       tile — the first letter of the item's label on the poolit gradient, the
       classic "no image → initials" pattern. Reads as intentional, and gives
       each manual line its own identity in a mixed list of photo items. -->
  <div
    class="manual-avatar"
    :style="rootStyle"
    role="img"
    :aria-label="alt || label"
  >
    <span v-if="monogram" class="manual-avatar__monogram">{{ monogram }}</span>
    <f7-icon
      v-else
      f7="bag_fill"
      class="manual-avatar__icon"
      :size="iconSize"
    ></f7-icon>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  // The line's display name (manualLabel). First grapheme becomes the monogram.
  label: {
    type: String,
    default: '',
  },
  // Rendered tile size in px — 64 in the list row, 80 in the detail sheet,
  // 48 in the totals list. The monogram scales with it.
  size: {
    type: Number,
    default: 64,
  },
  // Accessibility label for the tile (role=img). Falls back to the label.
  alt: {
    type: String,
    default: '',
  },
});

// First full grapheme (Array.from is surrogate-safe; Arabic letters are single
// code points). toUpperCase is a no-op for Arabic but capitalizes Latin labels.
const monogram = computed(() => {
  const trimmed = (props.label ?? '').trim();
  if (!trimmed) return '';
  return Array.from(trimmed)[0].toUpperCase();
});

const iconSize = computed(() => Math.round(props.size * 0.42));

const rootStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  fontSize: `${Math.round(props.size * 0.42)}px`,
}));
</script>

<style scoped>
.manual-avatar {
    position: relative;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: var(--poolit-radius-md, 8px);
    /* Soft top-left highlight over a diagonal brand gradient — adds depth so the
       tile feels lit, not flat, and stays on-brand (poolit purple ramp). */
    background:
        radial-gradient(125% 125% at 22% 16%, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0) 46%),
        linear-gradient(135deg,
            var(--poolit-primary-500, #8e5bfe) 0%,
            var(--poolit-primary-700, #6621e2) 55%,
            var(--poolit-primary-900, #31006f) 100%);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
    overflow: hidden;
    user-select: none;
}

.manual-avatar__monogram {
    font-weight: var(--poolit-font-weight-bold, 800);
    line-height: 1;
    letter-spacing: -0.01em;
    /* Keep any leading numeral latin (house rule: Western digits). */
    font-feature-settings: 'lnum' on;
}

.manual-avatar__icon {
    opacity: 0.92;
}
</style>
